import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'

import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { useAdminQuery } from '@/features/admin/api'
import { Podium } from '@/features/admin/components/AwardsPanel'
import { StandingsPanel } from '@/features/admin/components/StandingsPanel'
import type { Award, Candidate } from '@/features/admin/types'

import { useContestBoard } from '../api'
import { ContestControl } from '../ContestControl'
import { ContestPicker, SectionHead, text, useCandidates, useContestChoice } from './shared'

/**
 * Concours & tours — d'un concours à la fois.
 *
 * Dans l'ordre de la soirée : les tours (qui est sur scène, démarrer,
 * terminer, conduire le vote), puis le tableau privé (public 70 % + jury
 * 30 %, départage, proclamation, publication des scores, envoi aux
 * candidats, retours en arrière motivés), puis les prix et leur podium.
 */
export function ContestTab({
  eventId,
  can,
}: {
  eventId: string | undefined
  can: (permission: string) => boolean
}) {
  const { t, i18n } = useTranslation()
  const board = useContestBoard(eventId, true)
  const contests = board.data?.contests ?? []
  const [contest, choose] = useContestChoice(contests)
  const candidates = useCandidates(eventId, can('competition.candidate.read'))
  const awards = useAdminQuery<Award[]>(
    ['admin-awards', eventId],
    `/admin/events/${eventId}/awards`,
    eventId,
    { enabled: can('competition.award.read') },
  )
  const locale = i18n.language

  if (board.isPending) return <Skeleton className="h-40 w-full" />
  if (!contest) {
    return <p className="text-content-secondary">{t('conductor.cockpit.contest.none')}</p>
  }

  const own = (awards.data ?? []).filter(
    (award) => award.is_active && award.competition_category_id === contest.id,
  )
  // Les prix sans concours (« Best Mask »…) : listés une fois, sous le
  // concours affiché, pour ne pas les chercher ailleurs.
  const general = (awards.data ?? []).filter(
    (award) => award.is_active && !award.competition_category_id,
  )

  return (
    <div className="space-y-8">
      <ContestPicker contests={contests} value={contest.id} onChange={choose} />

      <section className="space-y-3">
        <SectionHead
          title={t('conductor.cockpit.contest.rounds', { contest: text(contest.name_i18n, locale) })}
          hint={t('conductor.cockpit.contest.roundsHint')}
        />
        <ContestControl eventId={eventId} can={can} contestId={contest.id} bare />
      </section>

      {can('voting.result.read') ? (
        <section className="space-y-3">
          <SectionHead
            title={t('conductor.cockpit.contest.standings')}
            hint={t('conductor.cockpit.contest.standingsHint')}
          />
          <StandingsPanel
            key={contest.id}
            eventId={eventId}
            competitionCategoryId={contest.id}
            title={text(contest.name_i18n, locale)}
            can={can}
            live
          />
        </section>
      ) : null}

      {can('competition.award.read') && own.length + general.length > 0 ? (
        <section className="space-y-3">
          <SectionHead
            title={t('conductor.cockpit.contest.awards')}
            hint={t('conductor.cockpit.contest.awardsHint')}
          />
          {[...own, ...general].map((award) => (
            <AwardCard
              key={award.id}
              award={award}
              eventId={eventId}
              candidates={(candidates.data ?? []).filter(
                (candidate) =>
                  ['approved', 'finalist', 'winner'].includes(candidate.status) &&
                  (!award.competition_category_id ||
                    candidate.competition_category_id === award.competition_category_id),
              )}
              can={can}
            />
          ))}
        </section>
      ) : null}
    </div>
  )
}

function AwardCard({
  award,
  eventId,
  candidates,
  can,
}: {
  award: Award
  eventId: string | undefined
  candidates: Candidate[]
  can: (permission: string) => boolean
}) {
  const { t, i18n } = useTranslation()
  // Replié par défaut : trois marches de formulaire par prix, sur un
  // téléphone, noieraient le tableau du concours juste au-dessus.
  const [open, setOpen] = useState(false)
  const revealed = award.results.some((row) => row.is_revealed)
  const prize = text(award.prize_i18n, i18n.language)
  return (
    <Card className="p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="break-words font-display text-heading-sm text-content-primary">
            {text(award.name_i18n, i18n.language)}
          </h3>
          {prize !== '—' ? (
            <p className="mt-0.5 break-words text-caption text-content-muted">{prize}</p>
          ) : null}
        </div>
        {revealed ? (
          <Badge tone="success">{t('admin.awards.revealed')}</Badge>
        ) : award.results.length > 0 ? (
          <Badge tone="champagne">{t('admin.awards.podiumReady')}</Badge>
        ) : null}
      </div>
      <Button className="mt-3" size="sm" variant="outline" onClick={() => setOpen((value) => !value)}>
        {open ? t('conductor.cockpit.contest.closePodium') : t('conductor.cockpit.contest.openPodium')}
      </Button>
      {open ? <Podium award={award} eventId={eventId} candidates={candidates} can={can} /> : null}
    </Card>
  )
}
